export type CellPosition = {
  row: number
  col: number
}

export type Board = string[][]

type RowCollection = ReadonlySet<number> | readonly number[]

const isWholeNumber = (value: number) =>
  Number.isFinite(value) && Math.floor(value) === value

export const cellKey = ({ row, col }: CellPosition) => `${row}:${col}`

export const parseCellKey = (key: string): CellPosition => {
  const match = /^(\d+):(\d+)$/.exec(key)
  if (!match) {
    throw new Error(`Invalid cell key: ${key}`)
  }

  return {
    row: Number(match[1]),
    col: Number(match[2]),
  }
}

const splitWord = (word: string) => Array.from(word)

export const isWordSquare = (words: readonly string[]) => {
  const size = words.length
  if (size === 0) {
    return false
  }

  const rows = words.map((word) => splitWord(word.toLocaleUpperCase()))
  if (rows.some((row) => row.length !== size)) {
    return false
  }

  for (let row = 0; row < size; row++) {
    for (let col = row + 1; col < size; col++) {
      if (rows[row][col] !== rows[col][row]) {
        return false
      }
    }
  }

  return true
}

export const createEmptyBoard = (size = 5): Board => {
  const safeSize = isWholeNumber(size) && size > 0 ? size : 0
  return Array.from({ length: safeSize }, () =>
    Array.from({ length: safeSize }, () => '')
  )
}

const hashString = (value: string) => {
  let hash = 2166136261
  for (let index = 0; index < value.length; index++) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return hash >>> 0
}

const seededRandom = (seed: number) => {
  let state = seed >>> 0
  return () => {
    state += 0x6d2b79f5
    let value = state
    value = Math.imul(value ^ (value >>> 15), value | 1)
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61)
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296
  }
}

const LETTER_INFORMATION_SCORE: Readonly<Record<string, number>> = {
  A: 1,
  B: 3,
  C: 3,
  D: 2,
  E: 1,
  F: 4,
  G: 2,
  H: 4,
  I: 1,
  J: 8,
  K: 5,
  L: 1,
  M: 3,
  N: 1,
  O: 1,
  P: 3,
  Q: 10,
  R: 1,
  S: 1,
  T: 1,
  U: 1,
  V: 4,
  W: 4,
  X: 8,
  Y: 4,
  Z: 10,
}

type CellPair = readonly [number, number]

const getPairings = (indexes: readonly number[]): CellPair[][] => {
  if (indexes.length === 0) return [[]]

  const [first, ...remaining] = indexes
  return remaining.flatMap((second, secondIndex) => {
    const rest = remaining.filter((_, index) => index !== secondIndex)
    return getPairings(rest).map((pairs) => [
      [first, second] as const,
      ...pairs,
    ])
  })
}

const addMirroredPair = (clues: Set<string>, [row, col]: CellPair) => {
  clues.add(cellKey({ row, col }))
  clues.add(cellKey({ row: col, col: row }))
}

const createSeededClueCells = (size: number, seed: string | number) => {
  const clues = new Set<string>()
  if (size === 0) return clues

  const random = seededRandom(hashString(String(seed)))
  const indexes = Array.from({ length: size }, (_, index) => index)

  for (let index = indexes.length - 1; index > 0; index--) {
    const swapIndex = Math.floor(random() * (index + 1))
    const current = indexes[index]
    indexes[index] = indexes[swapIndex]
    indexes[swapIndex] = current
  }

  let pairStart = 0
  if (size % 2 === 1) {
    const diagonal = indexes[0]
    clues.add(cellKey({ row: diagonal, col: diagonal }))
    pairStart = 1
  }

  for (let index = pairStart; index < size; index += 2) {
    addMirroredPair(clues, [indexes[index], indexes[index + 1]])
  }

  if (size >= 5) {
    const availablePairs: CellPair[] = []
    for (let row = 0; row < size; row++) {
      for (let col = row + 1; col < size; col++) {
        if (!clues.has(cellKey({ row, col }))) {
          availablePairs.push([row, col])
        }
      }
    }

    if (availablePairs.length > 0) {
      const extra = availablePairs[Math.floor(random() * availablePairs.length)]
      addMirroredPair(clues, extra)
    }
  }

  return clues
}

type InformativeClueLayout = {
  facts: CellPosition[]
  cellKeys: string[]
  signature: string
}

let informativeClueLayouts: InformativeClueLayout[] | undefined

const getInformativeClueLayouts = () => {
  if (informativeClueLayouts) return informativeClueLayouts

  const size = 5
  const allPairs: CellPair[] = []
  for (let row = 0; row < size; row++) {
    for (let col = row + 1; col < size; col++) {
      allPairs.push([row, col])
    }
  }

  const layouts: InformativeClueLayout[] = []

  for (let diagonal = 0; diagonal < size; diagonal++) {
    const remaining = Array.from({ length: size }, (_, index) => index).filter(
      (index) => index !== diagonal
    )

    for (const basePairs of getPairings(remaining)) {
      const basePairKeys = new Set(
        basePairs.map(([row, col]) =>
          cellKey({ row: Math.min(row, col), col: Math.max(row, col) })
        )
      )

      for (const extraPair of allPairs) {
        if (
          basePairKeys.has(cellKey({ row: extraPair[0], col: extraPair[1] }))
        ) {
          continue
        }

        const facts: CellPosition[] = [
          { row: diagonal, col: diagonal },
          ...basePairs.map(([row, col]) => ({
            row: Math.min(row, col),
            col: Math.max(row, col),
          })),
          { row: extraPair[0], col: extraPair[1] },
        ]
        const signature = facts.map(cellKey).sort().join('|')
        const cells = new Set<string>()
        cells.add(cellKey({ row: diagonal, col: diagonal }))
        basePairs.forEach((pair) => addMirroredPair(cells, pair))
        addMirroredPair(cells, extraPair)
        layouts.push({
          facts,
          cellKeys: Array.from(cells),
          signature,
        })
      }
    }
  }

  informativeClueLayouts = layouts
  return informativeClueLayouts
}

/**
 * Chooses the most useful legal 5x5 clue layout. The board shape stays the
 * same as before—one diagonal fact and three mirrored crossing facts—but the
 * letters are varied first and uncommon letters win otherwise-equal layouts.
 */
const createInformativeClueCells = (
  solution: readonly string[],
  seed: string | number
) => {
  const rows = solution.map((word) => splitWord(word.toLocaleUpperCase()))
  let best:
    | (InformativeClueLayout & {
        distinctLetters: number
        informationScore: number
        seededTieBreak: number
      })
    | undefined

  getInformativeClueLayouts().forEach((layout) => {
    const uniqueLetters = new Set(
      layout.facts.map(({ row, col }) => rows[row][col])
    )
    const candidate = {
      ...layout,
      distinctLetters: uniqueLetters.size,
      informationScore: Array.from(uniqueLetters).reduce(
        (score, letter) => score + (LETTER_INFORMATION_SCORE[letter] || 0),
        0
      ),
      seededTieBreak: hashString(`${seed}|${layout.signature}`),
    }

    if (
      !best ||
      candidate.distinctLetters > best.distinctLetters ||
      (candidate.distinctLetters === best.distinctLetters &&
        candidate.informationScore > best.informationScore) ||
      (candidate.distinctLetters === best.distinctLetters &&
        candidate.informationScore === best.informationScore &&
        candidate.seededTieBreak > best.seededTieBreak) ||
      (candidate.distinctLetters === best.distinctLetters &&
        candidate.informationScore === best.informationScore &&
        candidate.seededTieBreak === best.seededTieBreak &&
        candidate.signature < best.signature)
    ) {
      best = candidate
    }
  })

  return new Set(best?.cellKeys)
}

/**
 * Builds a small, symmetric set of starting clues. A valid 5x5 word square
 * gets seven cells representing four varied facts: one diagonal clue and
 * three mirrored crossing pairs. More than two thirds remains for the player.
 */
export const createClueCells = (
  solution: readonly string[],
  seed: string | number = solution.join('|')
) => {
  const size = solution.length
  return size === 5 && isWordSquare(solution)
    ? createInformativeClueCells(solution, seed)
    : createSeededClueCells(size, seed)
}

type ChooseHintCellsOptions = {
  board: readonly (readonly string[])[]
  solution: readonly string[]
  primaryCandidates: readonly CellPosition[]
  isEditable: (cell: CellPosition) => boolean
  selectedCell?: CellPosition
}

/**
 * Picks one useful cell and, when possible, its transposed word-square twin.
 * A player-selected cell wins; automatic hints favor a pair that helps two
 * rows, then a wrong entry, then a more informative solution letter.
 */
export const chooseHintCells = ({
  board,
  solution,
  primaryCandidates,
  isEditable,
  selectedCell,
}: ChooseHintCellsOptions): CellPosition[] => {
  const solutionRows = solution.map(splitWord)
  const candidateKeys = new Set(primaryCandidates.map(cellKey))
  const isUseful = (cell: CellPosition) =>
    isEditable(cell) &&
    solutionRows[cell.row]?.[cell.col] !== undefined &&
    board[cell.row]?.[cell.col] !== solutionRows[cell.row][cell.col]
  const getMirror = ({ row, col }: CellPosition): CellPosition => ({
    row: col,
    col: row,
  })
  const hasUsefulMirror = (cell: CellPosition) => {
    const mirror = getMirror(cell)
    return cellKey(mirror) !== cellKey(cell) && isUseful(mirror)
  }
  const usefulCandidates = primaryCandidates.filter(isUseful)
  const selectedIsUseful =
    selectedCell &&
    candidateKeys.has(cellKey(selectedCell)) &&
    isUseful(selectedCell)

  const primary = selectedIsUseful
    ? selectedCell
    : [...usefulCandidates].sort((first, second) => {
        const mirrorDifference =
          Number(hasUsefulMirror(second)) - Number(hasUsefulMirror(first))
        if (mirrorDifference) return mirrorDifference

        const firstIsWrong = Boolean(board[first.row]?.[first.col])
        const secondIsWrong = Boolean(board[second.row]?.[second.col])
        if (firstIsWrong !== secondIsWrong) {
          return Number(secondIsWrong) - Number(firstIsWrong)
        }

        const informationDifference =
          (LETTER_INFORMATION_SCORE[solutionRows[second.row]?.[second.col]] ||
            0) -
          (LETTER_INFORMATION_SCORE[solutionRows[first.row]?.[first.col]] || 0)
        if (informationDifference) return informationDifference

        return first.row - second.row || first.col - second.col
      })[0]

  if (!primary) return []

  const mirror = getMirror(primary)
  return hasUsefulMirror(primary) ? [primary, mirror] : [primary]
}

const hasRow = (rows: RowCollection, row: number) => {
  if (Array.isArray(rows)) {
    return rows.includes(row)
  }
  return (rows as ReadonlySet<number>).has(row)
}

const readSolutionCell = (
  solutionRows: readonly (readonly string[])[],
  row: number,
  col: number
) => solutionRows[row]?.[col]

const applyCellSet = (
  result: Board,
  solutionRows: readonly (readonly string[])[],
  cells: ReadonlySet<string>
) => {
  cells.forEach((key) => {
    let position: CellPosition
    try {
      position = parseCellKey(key)
    } catch {
      return
    }

    const value = readSolutionCell(solutionRows, position.row, position.col)
    if (
      value !== undefined &&
      result[position.row]?.[position.col] !== undefined
    ) {
      result[position.row][position.col] = value
    }
  })
}

/**
 * Returns a new board with immutable information copied from the solution.
 * Solved rows also reveal their crossing column, which is the core advantage
 * of completing a row in a word square.
 */
export const applyKnownCells = (
  board: readonly (readonly string[])[],
  solution: readonly string[],
  clueCells: ReadonlySet<string>,
  revealedCells: ReadonlySet<string>,
  solvedRows: RowCollection
): Board => {
  const size = solution.length
  const solutionRows = solution.map(splitWord)
  const result = Array.from({ length: size }, (_, row) =>
    Array.from({ length: size }, (_, col) => board[row]?.[col] ?? '')
  )

  applyCellSet(result, solutionRows, clueCells)
  applyCellSet(result, solutionRows, revealedCells)

  for (let solvedRow = 0; solvedRow < size; solvedRow++) {
    if (!hasRow(solvedRows, solvedRow)) {
      continue
    }

    for (let col = 0; col < size; col++) {
      const rowValue = readSolutionCell(solutionRows, solvedRow, col)
      const crossingValue = readSolutionCell(solutionRows, col, solvedRow)
      if (rowValue !== undefined) {
        result[solvedRow][col] = rowValue
      }
      if (crossingValue !== undefined) {
        result[col][solvedRow] = crossingValue
      }
    }
  }

  return result
}

export function getEditableCellsForRow(
  row: number,
  size: number,
  lockedCells?: ReadonlySet<string>,
  solvedRows?: RowCollection
): CellPosition[]
export function getEditableCellsForRow(
  row: number,
  size: number,
  clueCells: ReadonlySet<string>,
  revealedCells: ReadonlySet<string>,
  solvedRows: RowCollection
): CellPosition[]
export function getEditableCellsForRow(
  row: number,
  size: number,
  firstCells: ReadonlySet<string> = new Set<string>(),
  secondCellsOrSolvedRows:
    | ReadonlySet<string>
    | RowCollection = new Set<number>(),
  explicitSolvedRows?: RowCollection
) {
  if (!isWholeNumber(row) || !isWholeNumber(size) || row < 0 || row >= size) {
    return []
  }

  const hasSeparateCellSets = explicitSolvedRows !== undefined
  const lockedCells = new Set(firstCells)
  if (hasSeparateCellSets) {
    ;(secondCellsOrSolvedRows as ReadonlySet<string>).forEach((key) =>
      lockedCells.add(key)
    )
  }
  const solvedRows = hasSeparateCellSets
    ? explicitSolvedRows
    : (secondCellsOrSolvedRows as RowCollection)

  if (solvedRows && hasRow(solvedRows, row)) {
    return []
  }

  const editable: CellPosition[] = []
  for (let col = 0; col < size; col++) {
    const isCrossingFromSolvedRow = solvedRows ? hasRow(solvedRows, col) : false
    if (!isCrossingFromSolvedRow && !lockedCells.has(cellKey({ row, col }))) {
      editable.push({ row, col })
    }
  }

  return editable
}

type Direction = 1 | -1

const findNextCell = (
  cells: readonly CellPosition[],
  fromColumn: number | undefined,
  direction: Direction
) => {
  const ordered = [...cells].sort((first, second) => first.col - second.col)
  if (direction === -1) {
    ordered.reverse()
  }

  if (fromColumn === undefined) {
    return ordered[0] ?? null
  }

  return (
    ordered.find((cell) =>
      direction === 1 ? cell.col > fromColumn : cell.col < fromColumn
    ) ?? null
  )
}

export function getNextEditableCell(
  row: number,
  size: number,
  lockedCells?: ReadonlySet<string>,
  fromColumn?: number,
  direction?: Direction,
  solvedRows?: RowCollection
): CellPosition | null
export function getNextEditableCell(
  editableCells: readonly CellPosition[],
  current?: CellPosition | number | null,
  direction?: Direction
): CellPosition | null
export function getNextEditableCell(
  current: CellPosition | null,
  editableCells: readonly CellPosition[],
  direction?: Direction
): CellPosition | null
export function getNextEditableCell(
  first: number | readonly CellPosition[] | CellPosition | null,
  second?: number | readonly CellPosition[] | CellPosition | null,
  third:
    | ReadonlySet<string>
    | CellPosition
    | Direction
    | number
    | null
    | undefined = null,
  fourth?: number,
  fifth: Direction = 1,
  sixth: RowCollection = new Set<number>()
) {
  if (typeof first === 'number' && typeof second === 'number') {
    const lockedCells =
      third instanceof Set ? (third as ReadonlySet<string>) : new Set<string>()
    const editable = getEditableCellsForRow(first, second, lockedCells, sixth)
    return findNextCell(editable, fourth, fifth)
  }

  if (Array.isArray(first)) {
    const current = second as number | CellPosition | null | undefined
    const fromColumn =
      typeof current === 'number' ? current : current?.col ?? undefined
    const direction = (third === -1 ? -1 : 1) as Direction
    return findNextCell(first, fromColumn, direction)
  }

  const current = first as CellPosition | null
  const editable = (second ?? []) as readonly CellPosition[]
  const direction = (third === -1 ? -1 : 1) as Direction
  return findNextCell(editable, current?.col, direction)
}

export const choosePracticePuzzleIndexFromPool = (
  eligibleIndexes: readonly number[],
  current: number,
  recent: readonly number[] | ReadonlySet<number>,
  random: () => number = Math.random
) => {
  const allIndexes = Array.from(
    new Set(
      eligibleIndexes.filter((index) => Number.isInteger(index) && index >= 0)
    )
  )
  if (allIndexes.length === 0) {
    return -1
  }
  if (allIndexes.length === 1) {
    return allIndexes[0]
  }

  const recentSet = recent instanceof Set ? recent : new Set(recent)
  let candidates = allIndexes.filter(
    (index) => index !== current && !recentSet.has(index)
  )

  // Once every puzzle has appeared recently, still avoid immediately serving
  // the current puzzle again.
  if (candidates.length === 0) {
    candidates = allIndexes.filter((index) => index !== current)
  }
  if (candidates.length === 0) {
    candidates = allIndexes
  }

  const randomValue = random()
  const normalizedRandom = Number.isFinite(randomValue)
    ? Math.min(Math.max(randomValue, 0), 0.9999999999999999)
    : 0
  return candidates[Math.floor(normalizedRandom * candidates.length)]
}

export const choosePracticePuzzleIndex = (
  total: number,
  current: number,
  recent: readonly number[] | ReadonlySet<number>,
  random: () => number = Math.random
) => {
  const safeTotal = Number.isFinite(total) ? Math.max(0, Math.floor(total)) : 0
  return choosePracticePuzzleIndexFromPool(
    Array.from({ length: safeTotal }, (_, index) => index),
    current,
    recent,
    random
  )
}
